import { useMemo, useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";

import menuItems from "../../../utils/helpers/helperDataset/menuItems.json";
import IconHome from "../../../styles/icons/IconHome";
import DropdownMenuController from "./DropdownMenuController";
import { useOrganisationStatus } from "../../../context/organisationStatus";
import { ActiveColorGenerator } from "../../../utils/helpers/helperMethods/NavigationControllersHelper";

interface Props {
  handleSelectMenu: (name: string) => void;
  handleCustomerSelectMenu: (name: string) => void;
  selectedMenu: string;
  customerSelectedMenu: string;
}

type UserRole = "GlobalAdmin" | "Administrator" | "Operator" | "Customer";

interface Permission {
  GlobalAdmin?: boolean;
  Administrator?: boolean;
  Operator?: boolean;
  Customer?: boolean;
}

interface DropdownMenuItem {
  name: string;
  navigation: string;
  icon?: string;
  show?: boolean;
  permission?: Permission;
  url?: string;
}

interface MenuItem {
  name: string;
  navigation: string;
  icon?: string;
  isClickable?: boolean;
  show?: boolean;
  dropdown?: DropdownMenuItem[];
  dropdownShow?: boolean;
  permission?: Permission;
  url?: string;
}

const normalizePath = (path: string) => {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
};

const MenusController = ({
  handleSelectMenu,
  handleCustomerSelectMenu,
  selectedMenu,
  customerSelectedMenu,
}: Props) => {
  const [selectedDropdown, setSelectedDropdown] = useState("");
  const [selectedDropdownMenu, setSelectedDropdownMenu] = useState("");
  const menuRef = useRef<HTMLDivElement | null>(null);

  const { instance } = useMsal();
  const location = useLocation();
  const { isOrganisationActive } = useOrganisationStatus();

  const userRole = useMemo(() => {
    return String(
      (instance?.getActiveAccount()?.idTokenClaims as any)?.extension_ilpr ?? ""
    ) as UserRole;
  }, [instance]);

  const hasPermission = (permission?: Permission) => {
    if (!permission || !userRole) return false;
    return Boolean(permission[userRole]);
  };

  const visibleMenus = useMemo(() => {
    // Users of a deactivated organisation can only reach their profile.
    if (!isOrganisationActive) return [];
    return (menuItems as MenuItem[])
      .filter((menu) => menu.show !== false)
      .filter((menu) => hasPermission(menu.permission))
      .map((menu) => ({
        ...menu,
        dropdown: (menu.dropdown || [])
          .filter((item) => item.show !== false)
          .filter((item) => hasPermission(item.permission)),
      }))
      .filter((menu) => menu.isClickable || (menu.dropdown || []).length > 0);
  }, [userRole, isOrganisationActive]);

  const handleDropDown = (name: string) => {
    setSelectedDropdown((prev) => (prev === name ? "" : name));
  };

  const handleParentMenuClick = (menu: MenuItem) => {
    const hasDropdown = Boolean(menu.dropdownShow && menu.dropdown?.length);

    if (userRole === "Customer") {
      handleCustomerSelectMenu(menu.name);
    } else {
      handleSelectMenu(menu.name);
    }

    if (hasDropdown) {
      handleDropDown(menu.name);
    }
  };

  const handleDropdownMenuClick = (name: string) => {
    setSelectedDropdownMenu(name);
    setSelectedDropdown("");
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setSelectedDropdown("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const currentPath = normalizePath(location.pathname);

    const matchedParent = visibleMenus.find((menu) => {
      const menuPath = normalizePath(menu.navigation);

      if (menuPath === currentPath) return true;

      return (menu.dropdown || []).some(
        (item) => normalizePath(item.navigation) === currentPath
      );
    });

    if (matchedParent) {
      if (userRole === "Customer") {
        handleCustomerSelectMenu(matchedParent.name);
      } else {
        handleSelectMenu(matchedParent.name);
      }

      const matchedChild = (matchedParent.dropdown || []).find(
        (item) => normalizePath(item.navigation) === currentPath
      );

      if (matchedChild) {
        setSelectedDropdownMenu(matchedChild.name);
      } else {
        setSelectedDropdownMenu("");
      }
    }
  }, [
    location.pathname,
    visibleMenus,
    userRole,
    handleSelectMenu,
    handleCustomerSelectMenu,
  ]);

  const isMenuActive = (menu: MenuItem, currentSelectedMenu: string) => {
    const currentPath = normalizePath(location.pathname);
    const menuPath = normalizePath(menu.navigation);

    if (menuPath === "/" && currentPath === "/") return true;
    if (menuPath === currentPath) return true;

    const hasActiveChild = (menu.dropdown || []).some(
      (item) => normalizePath(item.navigation) === currentPath
    );

    if (hasActiveChild) return true;

    return currentSelectedMenu === menu.name;
  };

  const renderMenuContent = (menu: MenuItem, currentSelectedMenu: string) => {
    const isActive = isMenuActive(menu, currentSelectedMenu);
    const hasDropdown = Boolean(menu.dropdownShow && menu.dropdown?.length);
    const isDropdownOpen = selectedDropdown === menu.name;

    const innerContent = (
      <div className="menu-item">
        <div className="menu-item-left">
          {menu.icon ? (
            <div className="logo">
              <IconHome
                breadcrumb={menu.icon}
                activeColor={
                  isActive
                    ? "#4FD5F2"
                    : ActiveColorGenerator(currentSelectedMenu, menu.name)
                }
              />
            </div>
          ) : null}

          <div className={`menu-item-name ${isActive ? "active-menu" : ""}`}>
            {menu.name}
          </div>
        </div>

        {hasDropdown ? (
          <div className="dropdown-arrow">
            <IconHome
              breadcrumb="dropdown"
              activeColor={
                isDropdownOpen || isActive
                  ? "#4FD5F2"
                  : ActiveColorGenerator(currentSelectedMenu, menu.name)
              }
            />
          </div>
        ) : null}
      </div>
    );

    if (hasDropdown) {
      return (
        <div
          className="link-tag"
          onClick={() => handleParentMenuClick(menu)}
          style={{ cursor: "pointer" }}
        >
          {innerContent}
        </div>
      );
    }

    if (menu.isClickable === false) {
      return <div className="link-tag">{innerContent}</div>;
    }

    return (
      <Link
        to={menu.navigation}
        className="link-tag"
        onClick={() => handleParentMenuClick(menu)}
      >
        {innerContent}
      </Link>
    );
  };

  const currentSelectedMenu =
    userRole === "Customer" ? customerSelectedMenu : selectedMenu;

  return (
    <div className="menu-section" ref={menuRef}>
      {visibleMenus.map((menu, index) => {
        const hasDropdown = Boolean(menu.dropdownShow && menu.dropdown?.length);
        const activeClass = isMenuActive(menu, currentSelectedMenu);

        return (
          <div
            key={`${menu.name}-${
              menu.navigation || menu.url || "no-nav"
            }-${index}`}
            className={`list-styles ${activeClass ? "active-class" : ""}`}
          >
            {renderMenuContent(menu, currentSelectedMenu)}

            {hasDropdown ? (
              <DropdownMenuController
                menuItemName={menu.name}
                dropdownMenuList={menu.dropdown || []}
                isOpen={selectedDropdown === menu.name}
                handleSetSelectedDropdownMenu={handleDropdownMenuClick}
                selectedDropdownMenu={selectedDropdownMenu}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default MenusController;
